import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import { formatMinutes } from './timeFormat';
import type { AllocationInput, MilestoneInput, ProjectInput, TimeEntryInput, WorkItem } from './types';

export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (params: api.ProjectListParams) => [...projectKeys.lists(), params] as const,
  // Prefix shared by all detail queries — invalidating this matches a project
  // cached under EITHER its public_id (URL) or numeric pk (ADR-041), so nested
  // mutations (milestones) that only know the pk still refresh the open page.
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...projectKeys.all, 'detail', id] as const,
};

/** Truthy id that isn't the `-1` "skip" sentinel used by optional lookups. */
function isResolvable(id: string | number): boolean {
  return id !== '' && id !== -1 && id !== '-1' && id != null;
}

export function useProjects(params: api.ProjectListParams) {
  return useQuery({
    queryKey: projectKeys.list(params),
    queryFn: () => api.listProjects(params),
    placeholderData: keepPreviousData,
  });
}

export function useProject(id: string | number) {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => api.getProject(id),
    // Resolves by public_id (string, from the URL) or integer pk; the backend
    // accepts either (ADR-041).
    enabled: isResolvable(id),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectInput) => api.createProject(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.lists() });
      toast.success('Project created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

/** `notify: false` for a form that confirms the save in place (SaveButton,
 *  FE-ADR-065); inline edits and the form page keep the toast. */
export function useUpdateProject(id: string | number, { notify = true }: { notify?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<ProjectInput>) => api.updateProject(id, input),
    onSuccess: (project, input) => {
      qc.invalidateQueries({ queryKey: projectKeys.lists() });
      qc.setQueryData(projectKeys.detail(id), project);
      // Turning KB sync off purges the project's documents server-side; drop
      // the cached "In the Knowledge Base" state (DEV13-113).
      if ('kb_sync_enabled' in input) qc.invalidateQueries({ queryKey: ['kb-source-status'] });
      if (notify) toast.success('Project updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.deleteProject(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.lists() });
      toast.success('Project deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Budget ----
export function useProjectBudget(id: number) {
  return useQuery({
    queryKey: [...projectKeys.detail(id), 'budget'],
    queryFn: () => api.getProjectBudget(id),
    enabled: Number.isInteger(id) && id > 0,
  });
}

// ---- Time entries ----
export const timeEntryKeys = {
  all: ['time-entries'] as const,
  lists: () => [...timeEntryKeys.all, 'list'] as const,
  list: (p: api.TimeEntryListParams) => [...timeEntryKeys.lists(), p] as const,
};

export function useTimeEntries(params: api.TimeEntryListParams = {}) {
  return useQuery({
    queryKey: timeEntryKeys.list(params),
    queryFn: () => api.listTimeEntries(params),
    placeholderData: keepPreviousData,
  });
}

function invalidateTimeAndBudget(qc: ReturnType<typeof useQueryClient>, projectId?: number) {
  qc.invalidateQueries({ queryKey: timeEntryKeys.lists() });
  // The cross-project Worklogs page reads a separate `worklogs` query — refresh
  // it too so an added/edited/deleted row updates without a reload.
  qc.invalidateQueries({ queryKey: worklogKeys.all });
  // Work items expose `logged_hours` (the "Logged" total) — refresh so it stays
  // in sync after a worklog is added/edited/deleted.
  qc.invalidateQueries({ queryKey: workItemKeys.all });
  // Budget actuals depend on time entries — invalidate the matching project budget.
  if (projectId) qc.invalidateQueries({ queryKey: [...projectKeys.detail(projectId), 'budget'] });
}

export function useCreateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TimeEntryInput) => api.createTimeEntry(input),
    onSuccess: (entry) => {
      invalidateTimeAndBudget(qc, entry.project);
      toast.success('Time logged');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<TimeEntryInput> }) =>
      api.updateTimeEntry(id, input),
    onSuccess: (entry) => invalidateTimeAndBudget(qc, entry.project),
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; projectId?: number }) => api.deleteTimeEntry(id),
    onSuccess: (_void, vars) => {
      invalidateTimeAndBudget(qc, vars.projectId);
      toast.success('Time entry deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Allocations ----
export const allocationKeys = {
  all: ['allocations'] as const,
  lists: () => [...allocationKeys.all, 'list'] as const,
  list: (p: api.AllocationListParams) => [...allocationKeys.lists(), p] as const,
};

export function useAllocations(params: api.AllocationListParams = {}) {
  return useQuery({
    queryKey: allocationKeys.list(params),
    queryFn: () => api.listAllocations(params),
    placeholderData: keepPreviousData,
  });
}

export function useAllocationUtilization(asOf?: string) {
  return useQuery({
    queryKey: [...allocationKeys.all, 'utilization', asOf ?? 'today'],
    queryFn: () => api.getAllocationUtilization(asOf),
    placeholderData: keepPreviousData,
  });
}

export function useCreateAllocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AllocationInput) => api.createAllocation(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: allocationKeys.lists() });
      toast.success('Allocation added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateAllocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<AllocationInput> }) =>
      api.updateAllocation(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: allocationKeys.lists() }),
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteAllocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteAllocation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: allocationKeys.lists() });
      toast.success('Allocation removed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useCreateMilestone(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<MilestoneInput, 'project'>) =>
      api.createMilestone({ ...input, project: projectId }),
    onSuccess: () => {
      // Milestones are nested in the project read — refetch the detail. Use the
      // prefix so the page (cached under its public_id) refreshes too.
      qc.invalidateQueries({ queryKey: projectKeys.details() });
      toast.success('Milestone added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

// ---- Milestones (standalone, cross-project) ----
export const milestoneKeys = {
  all: ['milestones'] as const,
  lists: () => [...milestoneKeys.all, 'list'] as const,
  list: (p: api.MilestoneListParams) => [...milestoneKeys.lists(), p] as const,
};

export function useMilestones(params: api.MilestoneListParams = {}) {
  return useQuery({
    queryKey: milestoneKeys.list(params),
    queryFn: () => api.listMilestones(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateMilestoneStandalone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MilestoneInput) => api.createMilestone(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.lists() });
      qc.invalidateQueries({ queryKey: projectKeys.details() });
      toast.success('Milestone added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<MilestoneInput> }) =>
      api.updateMilestone(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.lists() });
      qc.invalidateQueries({ queryKey: projectKeys.details() });
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; projectId?: number }) => api.deleteMilestone(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: milestoneKeys.lists() });
      qc.invalidateQueries({ queryKey: projectKeys.details() });
      toast.success('Milestone deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}


// ---------------------------------------------------------------------------
// Native projects (ADR-071): config, work items, planning, worklogs
// ---------------------------------------------------------------------------
import type {
  CycleInput,
  LabelInput,
  ModuleInput,
  ProjectMemberInput,
  ProjectViewInput,
  StateInput,
  WorkItemInput,
  WorkItemRelationInput,
  WorkItemTypeInput,
} from './types';

const toastErr = (err: ApiError) => {
  if (!err.fieldErrors) toast.error(err.message);
};

// ---- Project archive / restore / progress ----
export function useArchiveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.archiveProject(id),
    onSuccess: (project) => {
      qc.invalidateQueries({ queryKey: projectKeys.lists() });
      qc.setQueryData(projectKeys.detail(project.public_id), project);
      toast.success('Project archived');
    },
    onError: toastErr,
  });
}

export function useRestoreProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.restoreProject(id),
    onSuccess: (project) => {
      qc.invalidateQueries({ queryKey: projectKeys.lists() });
      qc.setQueryData(projectKeys.detail(project.public_id), project);
      toast.success('Project restored');
    },
    onError: toastErr,
  });
}

export function useProjectProgress(id: string | number) {
  return useQuery({
    queryKey: [...projectKeys.detail(id), 'progress'],
    queryFn: () => api.getProjectProgress(id),
    enabled: isResolvable(id),
  });
}

export function useUploadProjectCover(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadProjectCover(id, file),
    onSuccess: (project) => {
      qc.setQueryData(projectKeys.detail(id), project);
      qc.setQueryData(projectKeys.detail(project.public_id), project);
      qc.invalidateQueries({ queryKey: projectKeys.lists() });
      toast.success('Cover updated');
    },
    onError: toastErr,
  });
}

export function useProjectActivity(id: string | number) {
  return useQuery({
    queryKey: [...projectKeys.detail(id), 'activity'],
    queryFn: () => api.listProjectActivity(id),
    enabled: isResolvable(id),
  });
}

// ---- Work items ----
export const workItemKeys = {
  all: ['work-items'] as const,
  lists: () => [...workItemKeys.all, 'list'] as const,
  list: (p: Record<string, unknown>) => [...workItemKeys.lists(), p] as const,
  // A work item is cached under whatever id its route used — public_id (drawer),
  // the readable slug of a shared link ("dev13-68-fix-…"), or the pk — so writes
  // go through `details()` to reach every copy (same idea as projectKeys).
  details: () => [...workItemKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...workItemKeys.details(), id] as const,
};

/** Put a freshly saved work item into every cached copy of it — without this a
 *  page opened by its slug kept showing the old values after an edit. */
export function syncWorkItemDetails(qc: QueryClient, item: WorkItem) {
  qc.setQueryData(workItemKeys.detail(item.public_id), item);
  qc.setQueriesData<WorkItem>({ queryKey: workItemKeys.details() }, (old) =>
    old && old.id === item.id ? item : old,
  );
}

export function useWorkItems(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: workItemKeys.list(params),
    queryFn: () => api.workItemsApi.list(params),
    placeholderData: keepPreviousData,
  });
}

export function useWorkItem(id: string | number) {
  return useQuery({
    queryKey: workItemKeys.detail(id),
    queryFn: () => api.workItemsApi.get(id),
    enabled: isResolvable(id),
  });
}

export function useCreateWorkItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkItemInput) => api.workItemsApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workItemKeys.lists() });
      toast.success('Work item created');
    },
    onError: toastErr,
  });
}

export function useUpdateWorkItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | number; input: Partial<WorkItemInput> }) =>
      api.workItemsApi.update(id, input),
    onSuccess: (item) => {
      qc.invalidateQueries({ queryKey: workItemKeys.lists() });
      syncWorkItemDetails(qc, item);
    },
    onError: toastErr,
  });
}

export function useDeleteWorkItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.workItemsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workItemKeys.lists() });
      toast.success('Work item deleted');
    },
    onError: toastErr,
  });
}

/** Board drag/transition — set state and/or reorder (sort_order). */
export function useMoveWorkItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string | number; body: { sort_order?: string; state?: number } }) =>
      api.workItemsApi.move(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: workItemKeys.lists() }),
    onError: toastErr,
  });
}

// ---- Generic child collections (states/labels/types/members/modules/cycles/views/relations) ----
function makeCollectionHooks<T, TInput>(
  scope: string,
  resource: {
    list: (p?: Record<string, unknown>) => Promise<{ results: T[] } | T[]>;
    create: (i: TInput) => Promise<T>;
    update: (id: string | number, i: Partial<TInput>) => Promise<T>;
    remove: (id: string | number) => Promise<void>;
  },
) {
  const keys = {
    all: [scope] as const,
    list: (p: Record<string, unknown>) => [scope, 'list', p] as const,
  };
  return {
    keys,
    useList: (params: Record<string, unknown> = {}, enabled = true) =>
      useQuery({
        queryKey: keys.list(params),
        queryFn: () => resource.list(params),
        placeholderData: keepPreviousData,
        enabled,
      }),
    useCreate: () => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: (input: TInput) => resource.create(input),
        onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
        onError: toastErr,
      });
    },
    useUpdate: () => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: ({ id, input }: { id: string | number; input: Partial<TInput> }) =>
          resource.update(id, input),
        onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
        onError: toastErr,
      });
    },
    useRemove: () => {
      const qc = useQueryClient();
      return useMutation({
        mutationFn: (id: string | number) => resource.remove(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
        onError: toastErr,
      });
    },
  };
}

export const statesHooks = makeCollectionHooks<import('./types').State, StateInput>('project-states', api.statesApi);
export const labelsHooks = makeCollectionHooks<import('./types').Label, LabelInput>('project-labels', api.labelsApi);
export const workItemTypesHooks = makeCollectionHooks<import('./types').WorkItemType, WorkItemTypeInput>('work-item-types', api.workItemTypesApi);
export const projectMembersHooks = makeCollectionHooks<import('./types').ProjectMember, ProjectMemberInput>('project-members', api.projectMembersApi);
export const projectLabelsHooks = makeCollectionHooks<import('./types').ProjectLabel, import('./types').ProjectLabelInput>('project-labels', api.projectLabelsApi);
export const modulesHooks = makeCollectionHooks<import('./types').Module, ModuleInput>('project-modules', api.modulesApi);
export const cyclesHooks = makeCollectionHooks<import('./types').Cycle, CycleInput>('project-cycles', api.cyclesApi);
export const projectViewsHooks = makeCollectionHooks<import('./types').ProjectView, ProjectViewInput>('project-views', api.projectViewsApi);
export const workItemRelationsHooks = makeCollectionHooks<import('./types').WorkItemRelation, WorkItemRelationInput>('work-item-relations', api.workItemRelationsApi);

// ---- Wiki pages (ADR-074) ----
export const pagesHooks = makeCollectionHooks<import('./types').WikiPage, import('./types').WikiPageInput>('wiki-pages', api.pagesApi);

export function usePageVersions(id: string | number | null) {
  return useQuery({
    queryKey: ['wiki-pages', 'versions', id],
    queryFn: () => api.pagesApi.versions(id as string | number),
    enabled: id != null,
  });
}

/** Page actions (restore/publish/archive) — invalidate the pages collection. */
export function usePageAction<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wiki-pages'] }),
    onError: toastErr,
  });
}

// ---- Worklogs report ----
export const worklogKeys = {
  all: ['worklogs'] as const,
  list: (p: api.WorklogListParams) => [...worklogKeys.all, 'list', p] as const,
  summary: (p: api.WorklogSummaryParams) => [...worklogKeys.all, 'summary', p] as const,
};

export function useWorklogs(params: api.WorklogListParams = {}) {
  return useQuery({
    queryKey: worklogKeys.list(params),
    queryFn: () => api.listWorklogs(params),
    placeholderData: keepPreviousData,
  });
}

export function useWorklogSummary(params: api.WorklogSummaryParams = {}, enabled = true) {
  return useQuery({
    queryKey: worklogKeys.summary(params),
    queryFn: () => api.getWorklogSummary(params),
    placeholderData: keepPreviousData,
    enabled,
  });
}

// ---- Work-item timer (ADR-076) ----
export const timerKeys = { current: ['timer', 'current'] as const };

export function useCurrentTimer() {
  return useQuery({
    queryKey: timerKeys.current,
    queryFn: () => api.getCurrentTimer(),
    // The bar reflects server state; the elapsed seconds tick client-side from
    // started_at, so no aggressive polling is needed. Refetch on focus catches
    // a timer started on another device/tab.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

function invalidateTimerAndTime(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: timerKeys.current });
  qc.invalidateQueries({ queryKey: timeEntryKeys.lists() });
  qc.invalidateQueries({ queryKey: worklogKeys.all });
  // Stopping a timer logs time → refresh work items' "Logged" total.
  qc.invalidateQueries({ queryKey: workItemKeys.all });
}

/** 404 — the timer was already stopped elsewhere; 409 — another was just
 *  started. Either way the cached timer is stale: refetch, so the note dialog
 *  (keyed to the timer id) closes instead of retrying into the same error. */
function refetchTimerIfStale(qc: ReturnType<typeof useQueryClient>, err: ApiError) {
  if (err?.status === 404 || err?.status === 409) {
    qc.invalidateQueries({ queryKey: timerKeys.current });
  }
}

export function useStartTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string | number;
      body?: { description?: string; billable?: boolean; previous_description?: string };
    }) => api.workItemsApi.startTimer(id, body ?? {}),
    onSuccess: () => {
      invalidateTimerAndTime(qc);
      toast.success('Timer started');
    },
    onError: (err: ApiError) => {
      refetchTimerIfStale(qc, err);
      toastErr(err);
    },
  });
}

export function useStopTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { description?: string } = {}) => api.stopTimer(body),
    onSuccess: (entry) => {
      invalidateTimerAndTime(qc);
      // Budget actuals depend on logged time.
      if (entry.project) qc.invalidateQueries({ queryKey: [...projectKeys.detail(entry.project), 'budget'] });
      toast.success(`Logged ${formatMinutes(entry.minutes)}`);
    },
    onError: (err: ApiError) => {
      refetchTimerIfStale(qc, err);
      toastErr(err);
    },
  });
}

export function useDiscardTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.discardTimer(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timerKeys.current });
      toast.success('Timer discarded');
    },
    onError: toastErr,
  });
}

// ---- Project attachments (ADR-078) ----
export const attachmentKeys = {
  all: ['project-attachments'] as const,
  list: (projectId: number) => [...attachmentKeys.all, projectId] as const,
};

export function useProjectAttachments(projectId: number) {
  return useQuery({
    queryKey: attachmentKeys.list(projectId),
    queryFn: () => api.listProjectAttachments(projectId),
    enabled: Number.isInteger(projectId) && projectId > 0,
  });
}

export function useUploadProjectAttachment(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadProjectAttachment(projectId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attachmentKeys.list(projectId) });
      toast.success('File uploaded');
    },
    onError: toastErr,
  });
}

export function useDeleteProjectAttachment(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.deleteProjectAttachment(publicId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attachmentKeys.list(projectId) });
      toast.success('File removed');
    },
    onError: toastErr,
  });
}
